import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ProjectAssetMappingService } from '@app/cem/project/content/project-asset-mapping/project-asset-mapping.service';
import { AssetMappingConfiguration, AssetTypeFilter, IModuleAllocation } from '@app/cem/project/models/asset-mapping-configuration';
import { WellsProjectAssetMapping } from '@app/cem/project/models/wells-project-asset-mapping';
import { GeneralProjectService } from '@app/cem/project/services/general-project.service';
import { ProjectModuleService } from '@app/cem/project/services/project-module.service';
import { ErrorService } from '@app/common/services/error.service';
import { DisabledControlDirective } from '@app/shared/directives/disabled-control/disabled-control.directive';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { AssetType, Project } from 'app/shared/models';
import { LookupPlusService } from '../../../services/lookups/lookup-plus.service';

@Component({
  selector: 'app-project-asset-mapping-well-details',
  templateUrl: './project-asset-mapping-well-details.component.html',
  styleUrls: ['./project-asset-mapping-well-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ComboBoxComponent,
    DecimalPipe,
    DisabledControlDirective,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class ProjectAssetMappingWellDetailsComponent implements OnInit {
  private projectModuleService = inject(ProjectModuleService);
  private generalProjectService = inject(GeneralProjectService);
  private lookupPlusService = inject(LookupPlusService);
  private errorService = inject(ErrorService);
  private formBuilder = inject(UntypedFormBuilder);

  private projectIdFilter = 'ProjectId eq';
  private superTypeFilter = 'Asset/AssetType/AssetSuperTypeId eq';
  private assetTypeFilter = 'Asset/AssetTypeId eq';
  private tProjectAssetMapping = 'ProjectAssetMappings';
  private tableProjectAssetMapping: string = 'ProjectAssetMappings';
  private scenarioWellsSelect = 'ScenarioWells?$select=ScenarioWellId&$expand=DrillGroup($select=DrillGroupId,Description),FracGroup($select=FracGroupId;$expand=CombinedFracGroup($select=Description))&$filter=ScenarioId eq ';
  private scenarioWellsFilter = 'and WellId eq ';
  private tankBatterySelect = 'Assets?$select=AssetName&$filter=TankBatteryAreaId eq';
  private tankBatteryFilter = 'and AssetTypeId eq 35 &$top=1';
  private assetTypeFilters: AssetTypeFilter[] = [];
  private isConstructedAsset: boolean = false;
  private allowAllocation: boolean = false;
  private isSingleInstanceAsset: boolean = false;
  private tempProjectTypeSingleInstance: number;
  private allocationPercentage: number;
  private projectAssetMappingId: number;
  private selectedAssetTypeId: number;
  private assetSuperTypeId: number;
  private tankBatteryAreaId = 0;
  private minFilterLength = 3;
  private assignedWellId: number;
  public workingInterest: number = null;
  public isInsideCityLimits: boolean = false;
  public well: any = {};
  public pad: any = {};
  public tankBattery: any = {};
  public moduleIsEditable: boolean = false;
  public showNoCostCenterMessage: boolean = false;
  public showMultipleAssetMessage: boolean = false;
  public multipleAssetMessage: string = 'More than one Well is currently mapped to this project';
  public owidMessage = `OWID is missing from Well Asset. It is still possible to complete this form and publish an Estimate. However an OWID is required for this project to integrate to WellView.`;
  public projectAssetMapping: any = {};
  public wellMapForm: UntypedFormGroup;
  public isInEditMode: boolean;
  public isOwidEditable: boolean;
  public isOwidValueMissing: boolean;
  public isWellReassignment: boolean;
  public isAssetSelected: boolean;
  public showToggleAllocation: boolean;
  public assetTypeList = new Array<AssetType>();
  public filteredAssetTypeList = new Array<AssetType>();
  public disabledAssetType: boolean = false;
  public disabledAssets: boolean = true;
  public assetNameList = new Array<any>();
  public assetIdList = new Array<any>();
  public wellSsnList = new Array<any>();

  readonly projectId = input<number>(undefined);
  readonly project = input<Project>(undefined);
  readonly projectTypeId = input<number>(undefined);
  readonly assetTeamId = input<number>(undefined);
  readonly scenarioId = input<number>(undefined);
  readonly assetMappingConfig = input<AssetMappingConfiguration>(undefined);
  readonly projectAssetMappingService = input<ProjectAssetMappingService>(undefined);

  readonly owidFlag = output<boolean>();

  constructor() {
    this.generateForm();
  }

  ngOnInit() {
    this.setValuesFromConfig();
    this.loadPageData();
    this.subscribeToProjectModuleEvents();
  }

  private generateForm() {
    this.wellMapForm = this.formBuilder.group({
      Asset: [null, [Validators.required]],
      AssetType: [null, [Validators.required]],
      Owid: [null]
    });
    this.wellMapForm.valueChanges.subscribe(data => this.onValueChanged(data));
    return this.wellMapForm;
  }

  private onValueChanged(data?: any) {
    if (!this.wellMapForm) {
      return;
    }
    if (this.wellMapForm.valid && this.wellMapForm.dirty) {
      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          allocationPercentage: this.allocationPercentage,
          hasAssets: true,
          isActiveToggle: true,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: true
        }
      );
    }
    this.validateOwid(this.well);
  }

  private loadPageData() {
    this.isInEditMode = false;
    this.loadAssetTypes();
    this.loadAssetMappingData();
  }

  private formatfilterString(): string {
    if (this.assetTypeFilters.length > 0) {
      let constructedFilter = '';
      this.assetTypeFilters.map((filter, i) => {
        let assetTypeFilter = `(${this.projectIdFilter} ${this.projectId()} and ${this.assetTypeFilter} ${filter.AssetTypeId})`;
        constructedFilter = constructedFilter + assetTypeFilter;
        if (this.assetTypeFilters.length - 1 !== i) {
          constructedFilter = constructedFilter + ' or ';
        }
      });
      return constructedFilter;
    }
    return `${this.projectIdFilter} ${this.projectId()} and ${this.superTypeFilter} ${this.assetSuperTypeId}`;
  }

  private loadAssetMappingData() {
    let queryString = `$filter=${this.formatfilterString()}`;
    this.lookupPlusService.getLookup(this.tProjectAssetMapping, queryString)
      .then(payload => {
        let assets = payload.value;
        this.projectAssetMapping = Object.assign({}, assets[0]);

        this.project().ProjectAssetMappings_Project = assets;
        let allocationPercentage = this.projectAssetMapping && this.projectAssetMapping.PercentAllocation;

        this.setToggleModule(this.projectAssetMapping.ProjectAssetMappingId ? true : false);
        this.projectAssetMappingService().setModuleAllocation(
          {
            assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
            moduleId: this.assetMappingConfig().ModuleId,
            allocationPercentage: allocationPercentage || 0,
            hasAssets: this.projectAssetMapping.ProjectAssetMappingId ? true : false,
            isActiveToggle: this.projectAssetMapping.ProjectAssetMappingId ? true : false,
            isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule
          }
        );

        if (assets.length > 1) {
          this.showMultipleAssetMessage = true;
        }
        if (this.projectAssetMapping.AssetId) {
          this.projectAssetMappingId = this.projectAssetMapping.ProjectAssetMappingId;
          this.loadWellData(this.projectAssetMapping.AssetId);
        }
      });
  }

  private loadWellData(assetId: number) {
    this.isOwidValueMissing = false;
    this.generalProjectService.getWellAssetMapped(assetId, this.scenarioId())
      .then(x => {
        this.well = new WellsProjectAssetMapping(x[0]);
        if (this.well) {
          this.assetNameList.push(this.well);
          this.assetIdList.push(this.well);
          this.wellSsnList.push(this.well);

          this.setWorkingInterest();
          this.setWellValues();
          this.loadPadData();
          this.loadTankBatteryData();
          this.wellMapForm.setValue({
            AssetType: { AssetTypeId: this.well.AssetTypeId, Description: this.well.AssetType },
            Asset: this.well,
            Owid: this.well.OWID
          });
          this.validateOwid(this.well);
        }
      });
  }

  private loadPadData() {
    let url = `${this.scenarioWellsSelect}${this.scenarioId()} ${this.scenarioWellsFilter}${this.well.WellId}`;
    let pad: any = {};
    this.lookupPlusService.getLookupNoCount(url)
      .then((jury) => {
        this.pad = Object.assign({}, jury.value[0]);
      });
  }

  private loadTankBatteryData() {
    if (!this.tankBatteryAreaId) return;

    let url = `${this.tankBatterySelect} ${this.well.TankBatteryAreaId} ${this.tankBatteryFilter}`;

    this.lookupPlusService.getLookupNoCount(url)
      .then((jury) => { this.tankBattery = Object.assign({}, jury.value[0]); });
  }

  private setAssetTypeDropDownList() {
    if (this.assetTypeFilters.length > 0) {
      this.filteredAssetTypeList = [];
      for (let item of this.assetTypeFilters) {
        let assetType = this.assetTypeList.find(a => a.AssetTypeId === item.AssetTypeId);
        this.filteredAssetTypeList.push(assetType);
      }
    }
    if (this.assetTypeFilters.length === 1) {
      let assetType = this.assetTypeList.find(a => a.AssetTypeId === this.assetTypeFilters[0].AssetTypeId);
      this.disabledAssetType = true;
      this.disabledAssets = false;
      this.selectedAssetTypeId = assetType.AssetTypeId;
      this.wellMapForm.patchValue({
        AssetType: {
          AssetTypeId: assetType.AssetTypeId,
          Description: assetType.Description
        }
      });
    }
  }

  private loadAssetTypes() {
    this.generalProjectService.getAssetTypes()
      .then(x => {
        this.assetTypeList = x;
        this.setAssetTypeDropDownList();
      });
  }

  private subscribeToProjectModuleEvents() {
    this.projectAssetMappingService().getModulesToReload()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.clearAllocation(data);
          }
        });
      });

    this.projectAssetMappingService().getModulesToSaveChanges()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.updateProjectAssetMapping();
          }
        });
      });

    this.projectAssetMappingService().getModulesToCancelChanges()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.cancelChanges();
          }
        });
      });
  }

  private clearAllocation(module: IModuleAllocation) {
    this.well = {};
    this.pad = {};
    this.tankBattery = {};
    this.workingInterest = null;
    this.showToggleAllocation = true;
    this.projectAssetMappingService().setModuleAllocation(module);
  }

  private setWorkingInterest() {
    this.workingInterest = this.well && this.well.ActualGrossWorkingInterest;

    if (!this.workingInterest || this.workingInterest === 0) {
      this.workingInterest = this.well && this.well.EstimatedNetWorkingInterest;
    }

    if (!this.workingInterest || this.workingInterest === 0) {
      this.workingInterest = this.well && this.well.PreliminaryNetWorkingInterest;
    }
  }

  private setWellValues() {
    this.assignedWellId = this.well && this.well.WellId;
    this.isInsideCityLimits = this.well && this.well.IsInsideCityLimits;
    this.tankBatteryAreaId = this.well && this.well.TankBatteryAreaId;
  }

  private setToggleModule(hasAsset: boolean) {
    if (this.assetMappingConfig().ModuleProperties.IsToggleModule) {
      this.showToggleAllocation = !hasAsset;
    }
  }

  private setValuesFromConfig() {
    let config = this.assetMappingConfig();

    if (config) {
      if (config.AssetTypeFilters) {
        this.assetTypeFilters = config.AssetTypeFilters;
      }
      this.allowAllocation = config.IsAllowAllocation && config.IsAllowAllocation;
      this.isConstructedAsset = config.IsConstructedModule && config.IsConstructedModule;
      this.isSingleInstanceAsset = config.IsSingleInstanceAsset && config.IsSingleInstanceAsset;
      this.tempProjectTypeSingleInstance = this.isSingleInstanceAsset ? this.projectTypeId() : null;
      this.assetSuperTypeId = config.AssetSuperTypeId;
      this.allocationPercentage = this.allowAllocation ? 1 : 0;
      this.isOwidEditable = config.IsOwidEditable ? true : false;
      this.moduleIsEditable = config.ModuleProperties.IsMappingEditable && config.ModuleProperties.IsMappingEditable;
      this.isWellReassignment = config.IsWellReassignment ? true : false;
    }
  }

  private validateOwid(well: any): void {

    if (well) {
      if (well.OWID !== null && well.OWID > 0) {
        this.isOwidValueMissing = false;
        this.setOwidFlag();
        return;
      }
    }
    this.isOwidValueMissing = true;
    this.setOwidFlag();
    return;
  }

  private setOwidFlag(): void {
    this.owidFlag.emit(this.isOwidValueMissing);
  }

  public toggleAllocationModule() {
    this.showToggleAllocation = false;
    this.projectAssetMappingService().toggleModuleAllocation(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
  }

  public handleAssetTypeSelectionChange(value) {
    if (value === undefined) {
      this.disabledAssets = true;
      this.selectedAssetTypeId = null;
    } else {
      this.selectedAssetTypeId = value.AssetTypeId;
      this.disabledAssets = false;
    }
  }

  public handelAssetNameSelectionChange(value) {
    if (value) {
      this.isAssetSelected = true;
      this.well = Object.assign({}, value);
      this.setWorkingInterest();
      this.assetIdList = [this.well];
      this.wellSsnList = [this.well];
    } else {
      this.isAssetSelected = false;
    }
  }

  public handelAssetIdSelectionChange(value) {
    if (value) {
      this.isAssetSelected = true;
      this.well = Object.assign({}, value);
      this.setWorkingInterest();
      this.assetNameList = [this.well];
      this.wellSsnList = [this.well];
    } else {
      this.isAssetSelected = false;
    }
  }

  public handelWellSsnSelectionChange(value) {
    if (value) {
      this.isAssetSelected = true;
      this.well = Object.assign({}, value);
      this.setWorkingInterest();
      this.assetNameList = [this.well];
      this.assetIdList = [this.well];
    } else {
      this.isAssetSelected = false;
    }
  }

  public handleAssetFilter(value) {
    if (value.length <= this.minFilterLength) {
      return this.assetNameList = [];
    }

    this.generalProjectService.getWellAssetNameContains(this.scenarioId(), this.assetTeamId(), this.selectedAssetTypeId, value, this.tempProjectTypeSingleInstance)
      .then(x => {
        this.assetNameList = x.map(t => { return new WellsProjectAssetMapping(t); });
        this.assetNameList.map(a => a.Name = a.AssetName);
      });
  }

  public handleAssetIdFilter(value) {
    if (value.length <= this.minFilterLength) {
      return this.assetIdList = [];
    }

    this.generalProjectService.getWellAssetIdContains(this.scenarioId(), this.assetTeamId(), this.selectedAssetTypeId, value, this.tempProjectTypeSingleInstance)
      .then(x => this.assetIdList = x.map(t => { return new WellsProjectAssetMapping(t); }));
  }

  public handleWellSsnFilter(value) {
    if (value.length <= this.minFilterLength) {
      return this.wellSsnList = [];
    }

    this.generalProjectService.getWellSsnContains(this.scenarioId(), this.assetTeamId(), this.selectedAssetTypeId, value, this.tempProjectTypeSingleInstance)
      .then(x => this.wellSsnList = x.map(t => { return new WellsProjectAssetMapping(t); }));
  }

  public editProjectAssetMapping(e): void {
    this.isInEditMode = true;
  }

  public cancelChanges() {
    this.loadPageData();
    if (this.wellMapForm) this.wellMapForm.reset();
  }

  public updateProjectAssetMapping() {
    if (this.assignedWellId === this.well.WellId && this.well.OWID !== this.wellMapForm.controls.Owid.value) {
      let editWell = { WellId: this.well.WellId, OWID: +this.wellMapForm.controls.Owid.value };

      this.projectModuleService.update(this.well.WellId, editWell, 'Wells')
        .then(() => this.well.OWID = editWell.OWID)
        .catch((error) => {
          this.errorService.error('Error updating OWID for Well', error);
        });
    }

    if (this.projectAssetMappingId === 0 || !this.projectAssetMappingId) {
      let newMapping: any = {
        ProjectId: this.projectId(),
        AssetId: this.wellMapForm.controls.Asset.value.AssetId,
        IsConstructedAsset: this.isConstructedAsset,
        PercentAllocation: this.allocationPercentage
      };
      let form = JSON.stringify(newMapping);
      this.projectModuleService.create(form, this.tableProjectAssetMapping)
        .then(() => {
          this.loadPageData();
          if (this.wellMapForm) this.wellMapForm.reset();
        })
        .catch((error) => {
          this.errorService.error('Error creating asset mappping for well details', error);
          this.loadPageData();
        });
    }
    else {
      let assetId = this.wellMapForm.controls.Asset.value.AssetId;
      if (this.wellMapForm) this.wellMapForm.reset();

      let reassignment: any = {
        CurrentWellId: this.assignedWellId,
        NewWellId: this.well.WellId,
        ProjectId: this.projectId(),
        ScenarioId: this.scenarioId()
      };

      let updateMapping: any = { AssetId: assetId, PercentAllocation: this.allocationPercentage };
      let form = JSON.stringify(updateMapping);
      this.projectModuleService.update(this.projectAssetMappingId, form, this.tableProjectAssetMapping)
        .then(() => {
          if (this.isWellReassignment && this.assignedWellId !== this.well.WellId)
            this.generalProjectService.postProjectWellReassignment(reassignment).subscribe();
          this.loadPageData();
        })
        .catch((error) => {
          this.errorService.error('Error updating asset mappping for well details', error);
          this.loadPageData();
        });
    }
  }
  
  getPadDescription() {
    if (this.pad?.FracGroup?.CombinedFracGroup?.Description) {
      return this.pad.FracGroup.CombinedFracGroup.Description;
    } else if (this.pad?.DrillGroup?.Description) {
      return this.pad.DrillGroup.Description;
    } else {
      return '';
    }
  }
}